/**
 * A hop's flavor scores from its `flavorTags` (PLAN-FLAVOR D-FL4).
 *
 * The tags were written for type-ahead, in order of prominence, so position
 * is the intensity: the first two are what the hop is, the next two what it
 * also does, the rest a hint. Every tag the catalog uses is either mapped
 * here or ignored on purpose; the test fails on a new word until someone
 * decides which.
 */

import type { AxisScores, HopAxis } from '@beerwright/core';

export const HOP_TAG_AXES: Readonly<Record<string, readonly HopAxis[]>> = {
  // citrus
  citrus: ['citrus'],
  lemon: ['citrus'],
  lime: ['citrus'],
  orange: ['citrus'],
  grapefruit: ['citrus'],
  'pink-grapefruit': ['citrus'],
  tangerine: ['citrus'],
  mandarin: ['citrus'],
  tangelo: ['citrus'],
  pomelo: ['citrus'],
  yuzu: ['citrus'],
  'lemon-zest': ['citrus'],
  'lemon-peel': ['citrus'],
  'orange-peel': ['citrus'],
  'bitter-orange': ['citrus'],
  marmalade: ['citrus'],
  'orange-marmalade': ['citrus'],
  zesty: ['citrus'],
  lemongrass: ['citrus', 'herbal'],
  'orange-blossom': ['citrus', 'floral'],
  // tropical
  tropical: ['tropical'],
  pineapple: ['tropical'],
  passionfruit: ['tropical'],
  mango: ['tropical'],
  guava: ['tropical'],
  papaya: ['tropical'],
  lychee: ['tropical'],
  coconut: ['tropical'],
  'fruit-punch': ['tropical'],
  creamsicle: ['tropical'],
  melon: ['tropical'],
  watermelon: ['tropical'],
  honeydew: ['tropical'],
  banana: ['tropical'],
  // stone and orchard fruit
  'stone-fruit': ['stoneFruit'],
  peach: ['stoneFruit'],
  apricot: ['stoneFruit'],
  nectarine: ['stoneFruit'],
  cherry: ['stoneFruit'],
  pear: ['stoneFruit'],
  apple: ['stoneFruit'],
  'orchard-fruit': ['stoneFruit'],
  fruity: ['stoneFruit'],
  'sweet-fruit': ['stoneFruit'],
  'soft-fruit': ['stoneFruit'],
  'candied-fruit': ['stoneFruit'],
  // berry and vinous
  berry: ['berry'],
  'red-berry': ['berry'],
  'dark-berry': ['berry'],
  blackcurrant: ['berry'],
  redcurrant: ['berry'],
  currant: ['berry'],
  strawberry: ['berry'],
  raspberry: ['berry'],
  blueberry: ['berry'],
  blackberry: ['berry'],
  boysenberry: ['berry'],
  gooseberry: ['berry'],
  grape: ['berry'],
  'white-grape': ['berry'],
  'white-wine': ['berry'],
  'dark-fruit': ['berry'],
  raisin: ['berry'],
  // floral
  floral: ['floral'],
  rose: ['floral'],
  lavender: ['floral'],
  jasmine: ['floral'],
  lilac: ['floral'],
  honeysuckle: ['floral'],
  elderflower: ['floral'],
  'apple-blossom': ['floral'],
  // herbal
  herbal: ['herbal'],
  mint: ['herbal'],
  menthol: ['herbal'],
  eucalyptus: ['herbal'],
  sage: ['herbal'],
  dill: ['herbal'],
  tea: ['herbal'],
  'green-tea': ['herbal'],
  'black-tea': ['herbal'],
  botanical: ['herbal'],
  anise: ['herbal'],
  aniseed: ['herbal'],
  // spice
  spicy: ['spice'],
  spice: ['spice'],
  pepper: ['spice'],
  'black-pepper': ['spice'],
  clove: ['spice'],
  cardamom: ['spice'],
  ginger: ['spice'],
  saffron: ['spice'],
  noble: ['floral', 'herbal', 'spice'],
  // grassy and vegetal
  grassy: ['grassy'],
  grass: ['grassy'],
  hay: ['grassy'],
  green: ['grassy'],
  'green-pepper': ['grassy'],
  onion: ['grassy'],
  garlic: ['grassy'],
  // resin and dank
  pine: ['resinPine'],
  resin: ['resinPine'],
  resinous: ['resinPine'],
  dank: ['resinPine'],
  sticky: ['resinPine'],
  pungent: ['resinPine'],
  // earthy and woody
  earthy: ['earthy'],
  woody: ['earthy'],
  wood: ['earthy'],
  oak: ['earthy'],
  cedar: ['earthy'],
  leather: ['earthy'],
  cognac: ['earthy'],
};

/** Tags that say nothing about which axis: intensity words, and sweet notes no hop axis carries. */
export const HOP_TAGS_IGNORED: ReadonlySet<string> = new Set([
  'clean',
  'neutral',
  'intense',
  'concentrated',
  'bitter',
  'candy',
  'honey',
  'sweet',
  'vanilla',
  'chocolate',
  'molasses',
]);

function positionScore(index: number): number {
  return index < 2 ? 4 : index < 4 ? 3 : 2;
}

export function hopRuleScores(flavorTags: readonly string[]): AxisScores<'hop'> {
  const scores: Partial<Record<HopAxis, number>> = {};
  flavorTags.forEach((tag, index) => {
    for (const axis of HOP_TAG_AXES[tag] ?? []) {
      const here = positionScore(index);
      const before = scores[axis];
      scores[axis] = before === undefined ? here : Math.min(5, Math.max(before, here) + 1);
    }
  });
  return scores;
}
